import React, { useState, useEffect } from 'react';
import { Mail, Loader2, Save, Bell, Truck, AlertTriangle, UserCheck, FileText } from 'lucide-react';

const NotificationSettingsTab = () => {
  const [settings, setSettings] = useState({
    additional_emails: [],
    notify_on_approval_request: true,
    notify_on_approval_decision: true,
    notify_on_oc_creation: true,
    notify_on_overdue_po: true,
    notify_on_dispatch_status: true,
  });
  const [myPrefs, setMyPrefs] = useState({
    inapp_dispatch_status: true,
    inapp_overdue_po: true,
    inapp_approval_request: true,
    inapp_oc_creation: true,
  });
  const [eventTypes, setEventTypes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [savedMatrix, setSavedMatrix] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedPrefs, setSavedPrefs] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('prismoline_token')}`,
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
    const admin = user.role === 'Admin';
    setIsAdmin(admin);

    const fetches = [
      fetch(`${API_BASE}/settings/notifications`, { headers: getHeaders() }).then(r => r.json()).catch(() => null),
      fetch(`${API_BASE}/settings/notifications/my-preferences`, { headers: getHeaders() }).then(r => r.json()).catch(() => null),
    ];
    if (admin) {
      fetches.push(fetch(`${API_BASE}/settings/notifications/event-types`, { headers: getHeaders() }).then(r => r.json()).catch(() => null));
      fetches.push(fetch(`${API_BASE}/settings/notifications/assignments`, { headers: getHeaders() }).then(r => r.json()).catch(() => null));
    }
    Promise.all(fetches).then(([globalSettings, userPrefs, types, matrix]) => {
      if (globalSettings && !globalSettings.detail) setSettings(globalSettings);
      if (userPrefs && !userPrefs.detail) setMyPrefs({
        inapp_dispatch_status: userPrefs.inapp_dispatch_status ?? true,
        inapp_overdue_po: userPrefs.inapp_overdue_po ?? true,
        inapp_approval_request: userPrefs.inapp_approval_request ?? true,
        inapp_oc_creation: userPrefs.inapp_oc_creation ?? true,
      });
      if (Array.isArray(types)) setEventTypes(types);
      if (Array.isArray(matrix)) setAssignments(matrix);
      setLoading(false);
    });
  }, []);

  const handleSaveGlobal = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/settings/notifications`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify(settings),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      const res = await fetch(`${API_BASE}/settings/notifications/my-preferences`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify(myPrefs),
      });
      if (res.ok) { setSavedPrefs(true); setTimeout(() => setSavedPrefs(false), 2000); }
    } catch (err) { console.error(err); }
    setSavingPrefs(false);
  };

  const toggleAssign = (userId, key) => {
    setAssignments(prev => prev.map(a => a.user_id === userId
      ? { ...a, events: a.events.includes(key) ? a.events.filter(e => e !== key) : [...a.events, key] }
      : a));
  };

  const handleSaveMatrix = async () => {
    setSavingMatrix(true);
    try {
      const res = await fetch(`${API_BASE}/settings/notifications/assignments`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify(assignments.map(a => ({ user_id: a.user_id, events: a.events }))),
      });
      if (res.ok) { setSavedMatrix(true); setTimeout(() => setSavedMatrix(false), 2000); }
    } catch (err) { console.error(err); }
    setSavingMatrix(false);
  };

  const Toggle = ({ checked, onChange, testId }) => (
    <button onClick={onChange} data-testid={testId}
      className={`w-10 h-5 rounded-full transition-colors flex items-center ${checked ? 'bg-emerald-500 justify-end' : 'bg-gray-300 justify-start'}`}>
      <div className="w-4 h-4 bg-white rounded-full shadow mx-0.5" />
    </button>
  );

  const emailToggles = [
    { key: 'notify_on_approval_request', label: 'Customer Approval Requests', desc: 'Email when a new customer add/edit request is submitted', icon: UserCheck },
    { key: 'notify_on_approval_decision', label: 'Approval Decisions', desc: 'Email the submitter when their request is approved or rejected', icon: UserCheck },
    { key: 'notify_on_oc_creation', label: 'Order Confirmation Created', desc: 'Email when a new Order Confirmation is created', icon: FileText },
    { key: 'notify_on_overdue_po', label: 'Overdue Purchase Orders', desc: 'Email alerts for overdue PO deliveries', icon: AlertTriangle },
    { key: 'notify_on_dispatch_status', label: 'Dispatch Status Changes', desc: 'Email when a dispatch moves to In Transit or Delivered', icon: Truck },
  ];

  const inAppToggles = [
    { key: 'inapp_dispatch_status', label: 'Dispatch Status Updates', desc: 'In-app alerts when a dispatch moves to In Transit or Delivered', icon: Truck, color: '#1565C0' },
    { key: 'inapp_overdue_po', label: 'Overdue PO Alerts', desc: 'In-app alerts for overdue purchase order deliveries', icon: AlertTriangle, color: '#E65100' },
    { key: 'inapp_approval_request', label: 'Approval Requests', desc: 'In-app alerts for customer approval requests', icon: UserCheck, color: '#7B1FA2' },
    { key: 'inapp_oc_creation', label: 'OC Creation', desc: 'In-app alerts when new Order Confirmations are created', icon: FileText, color: '#0d6b3e' },
  ];

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      {/* My In-App Notification Preferences */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#24549c12' }}>
            <Bell className="w-4 h-4" style={{ color: '#24549c' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>My In-App Notifications</h3>
            <p className="text-[11px] text-gray-500">Choose which notifications appear in your bell icon</p>
          </div>
        </div>
        <div className="p-5 space-y-2">
          {inAppToggles.map(t => {
            const Icon = t.icon;
            return (
              <div key={t.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`pref-${t.key}`}>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.color + '14' }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{t.label}</p>
                    <p className="text-[11px] text-gray-500">{t.desc}</p>
                  </div>
                </div>
                <Toggle checked={myPrefs[t.key]} testId={`toggle-${t.key}`}
                  onChange={() => setMyPrefs({ ...myPrefs, [t.key]: !myPrefs[t.key] })} />
              </div>
            );
          })}
        </div>
        <div className="px-5 pb-4 flex justify-end">
          <button onClick={handleSavePrefs} disabled={savingPrefs} data-testid="save-my-prefs-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            {savingPrefs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : savedPrefs ? <><Save className="w-3.5 h-3.5" /> Saved!</> : <><Save className="w-3.5 h-3.5" /> Save Preferences</>}
          </button>
        </div>
      </div>

      {/* Global Email Notifications (Admin Only) */}
      {isAdmin && (
        <>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-50">
                <Mail className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Email Notifications</h3>
                <p className="text-[11px] text-gray-500">Organization-wide email notification settings (Admin only)</p>
              </div>
              <span className="ml-auto px-2 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full">ADMIN</span>
            </div>
            <div className="p-5 space-y-2">
              {emailToggles.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`email-toggle-${t.key}`}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{t.label}</p>
                        <p className="text-[11px] text-gray-500">{t.desc}</p>
                      </div>
                    </div>
                    <Toggle checked={settings[t.key]} testId={`toggle-email-${t.key}`}
                      onChange={() => setSettings({ ...settings, [t.key]: !settings[t.key] })} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* User-wise Email Assignments (replaces the old shared recipients list) */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-50">
                <UserCheck className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>User-wise Email Assignments</h3>
                <p className="text-[11px] text-gray-500">Tick which email notifications each user ID receives. Unticked users get none. Requesters always get their own approval outcomes.</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="assignment-matrix">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-2.5 font-bold text-gray-700 sticky left-0 bg-gray-50 min-w-[180px]">User</th>
                    {eventTypes.map(t => (
                      <th key={t.key} className="px-2 py-2.5 font-semibold text-gray-600 text-center min-w-[90px]">
                        <span className="block leading-tight">{t.label}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(u => (
                    <tr key={u.user_id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`assign-row-${u.user_id}`}>
                      <td className="px-4 py-2 sticky left-0 bg-white">
                        <p className="font-semibold text-gray-800">{u.name}</p>
                        <p className="text-[10px] text-gray-400">{u.email} · {u.role}</p>
                      </td>
                      {eventTypes.map(t => (
                        <td key={t.key} className="text-center px-2 py-2">
                          <input type="checkbox" data-testid={`assign-${u.user_id}-${t.key}`}
                            checked={u.events.includes(t.key)}
                            onChange={() => toggleAssign(u.user_id, t.key)}
                            className="w-4 h-4 accent-teal-600 cursor-pointer" />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {assignments.length === 0 && (
                    <tr><td colSpan={eventTypes.length + 1} className="px-4 py-6 text-center text-gray-400 italic">No active users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
              <button onClick={handleSaveMatrix} disabled={savingMatrix} data-testid="save-matrix-btn"
                className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: '#0d7a6c' }}>
                {savingMatrix ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : savedMatrix ? <><Save className="w-3.5 h-3.5" /> Saved!</> : <><Save className="w-3.5 h-3.5" /> Save Assignments</>}
              </button>
            </div>
          </div>

          {/* Save Global Settings */}
          <div className="flex justify-end">
            <button onClick={handleSaveGlobal} disabled={saving} data-testid="save-notification-settings"
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <><Save className="w-3.5 h-3.5" /> Saved!</> : <><Save className="w-3.5 h-3.5" /> Save Email Settings</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettingsTab;
